import React, {useState, lazy, Suspense, memo, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

/* Constants */
import {PRODUCT_DETAIL_CAROUSEL_IMAGE_SIZES} from '../../../../modules/product/productDesktopConstants'
import {DAVINCI_EVENT_NAMES} from '../../../../modules/analytics/davinci/davinciConstants'
import {
  ADULT_PRODUCT_IMAGE_PLACEHOLDER_SIZES,
  ADULT_PRODUCT_IMAGE_PLACEHOLDER_VIEW_TYPES
} from '../../adult/image/AdultProductImagePlaceholderConstants'

/* Components */
import CssCarousel from '../../../../components/CssCarousel/CssCarousel'
import HbPlaceholderFrame from '../../../../components/Placeholder/frame/HbPlaceholderFrame'
import AdultProductImagePlaceholder from '../../adult/image/AdultProductImagePlaceholder'
import HbImageView from '../../../../components/HbImage/view/HbImageView'
import VideoView from '../../../../components/VideoView/VideoView'
import ProductDetailCarouselThumbnails from './thumbnails/ProductDetailCarouselThumbnails'

/* Utils */
import {useEffectOnlyOnUpdate} from '../../../../common/react/hook/lifecycle'
import {yieldToMain} from '../../../../utils/performance'

/* Actions */
import {actionCreators as analyticsActionCreators} from '../../../../redux/actions/analyticsActions'

/* Styles */
import s from './product-detail-carousel.scss'

/* Lazy Components */
const ProductDetailExtendedCarousel = lazy(() => import('./extended/ProductDetailExtendedCarousel'))
const CssCarouselBadges = lazy(() => import('./badges/CssCarouselBadges'))

const ProductDetailCarousel = memo(({
  dispatch,
  className,
  name,
  merchantName,
  merchantId,
  productId,
  listingId,
  imageBadge,
  sku,
  categories,
  isAdultProduct,
  productMedia,
  pageTypeForDavinci,
  categoryName,
  categoryId,
  componentData,
  price,
  isFashionProduct,
  isAvailableProduct
}) => {
  const [videoPlay, setVideoPlay] = useState(false)
  const [showVideo, setShowVideo] = useState(false)
  const [showExtendedCarousel, setShowExtendedCarousel] = useState(false)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [sendVideoViewEvent, setSendVideoViewEvent] = useState(true)
  const [viewedProductImageIndexes] = useState({
    default: {},
    full: {}
  })

  const incentiveToBuyEventData = useMemo(() => ({
    tagName: showExtendedCarousel ? undefined : imageBadge?.tagName,
    imageLabel: showExtendedCarousel ? undefined : imageBadge?.text,
    type: imageBadge?.type,
  }), [imageBadge, showExtendedCarousel])

  useEffect(() => {
    setActiveSlideIndex(0)
    handleChangeActiveSlideIndex(0)
  }, [sku])

  useEffectOnlyOnUpdate(() => {
    handleChangeActiveSlideIndex(activeSlideIndex)
  }, [activeSlideIndex])

  useEffectOnlyOnUpdate(() => {
    if (!showExtendedCarousel) {
      let selectItem = document.getElementById(`pdp-carousel__slide${activeSlideIndex}`);
      selectItem?.scrollIntoView({behavior: 'smooth', block: 'end'});
    }
  }, [showExtendedCarousel])

  useEffectOnlyOnUpdate(async() => {
    if (videoPlay && sendVideoViewEvent) {
      setSendVideoViewEvent(false)
      window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.VIDEO_COMPONENT_VIEW, {
        product_id: productId,
        merchant_name: merchantName,
        category_id: categories?.[categories.length - 1]?.categoryId || '',
        category_name: categories?.[categories.length - 1]?.categoryName || '',
        page_value: sku,
        merchant_id: merchantId,
        sku: sku || '',
        page_type: pageTypeForDavinci,
        location: 'full',
        title: '',
        total_count: '',
        description: '',
        position: activeSlideIndex + 1,
        videoId: '',
        component_id: '',
      })
      await yieldToMain()
      window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.DISPLAY_VIDEO, {
        videoId: '',
        videoDuration: '',
        description: '',
        timestamp: '',
        product_id: productId,
        page_value: sku,
        merchant_id: merchantId,
        sku: sku || '',
        page_type: pageTypeForDavinci,
        location: 'full',
        title: '',
        total_count: '',
        component_id: '',
      })
    }
  }, [videoPlay])

  useEffectOnlyOnUpdate(() => {
    if (showVideo) {
      document.getElementsByTagName('body')[0].style.overflow = 'hidden'
    } else {
      document.getElementsByTagName('body')[0].style.overflow = 'initial'
    }
  }, [showVideo])

  useEffect(() => {
    if (viewedProductImageIndexes[showExtendedCarousel ? 'full' : 'default'][activeSlideIndex]) {
      return
    }
    dispatch(
      analyticsActionCreators.sendImageViewEvent.trigger({
        merchantName,
        merchantId,
        pageValue: sku,
        pageType: 'pdp',
        categoryId,
        categoryName,
        position: activeSlideIndex + 1,
        location: showExtendedCarousel ? 'full' : 'default',
        productId,
        sku,
        ...incentiveToBuyEventData,
      })
    )

    viewedProductImageIndexes[showExtendedCarousel ? 'full' : 'default'][activeSlideIndex] = true
  }, [activeSlideIndex])

  const handleChangeActiveSlideIndex = (index) => {
    let selectItem = document.getElementById(`pdp-carousel__slide${activeSlideIndex}`);
    selectItem?.scrollIntoViewIfNeeded({behavior: 'smooth', block: 'end'});

    let selectItemDetail = document.getElementById(`pdpDetail-carousel__slide${activeSlideIndex}`);
    selectItemDetail?.scrollIntoViewIfNeeded({behavior: 'smooth', block: 'end'});
  }

  const handleVideoPlayChange = async(isPlay, type) => {
    setVideoPlay(isPlay)
    await yieldToMain()
    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.LINK_CLICK, {
      page_type: pageTypeForDavinci,
      page_value: sku,
      location: 'video',
      text: isPlay ? 'Play' : 'Pause',
      placement: 'button',
      position: '',
      url: ''
    })
  }

  const handleExtendedCarouselClick = () => {
    setShowExtendedCarousel(true)
  }

  const handleExtendedCarouselCloseClick = () => {
    setShowExtendedCarousel(false)
  }

  const handleSlideChange = (index) => {
    setActiveSlideIndex(index)
  }

  const imageSize = PRODUCT_DETAIL_CAROUSEL_IMAGE_SIZES[componentData?.imageRatio]
  const imageSizeText = `${imageSize?.width}-${imageSize?.height}`
  const imageRatio = imageSize?.width / imageSize?.height
  const hasVideo = productMedia?.filter(image => image?.isVideo)?.length > 0

  return (
    <>
      <div className={cx(s.root, className)}>
        {
          isAdultProduct ?
            <AdultProductImagePlaceholder
              viewType={ADULT_PRODUCT_IMAGE_PLACEHOLDER_VIEW_TYPES.COLORED}
              size={ADULT_PRODUCT_IMAGE_PLACEHOLDER_SIZES.MEDIUM}
              ratio={imageRatio}/> :
            <>
              <CssCarousel
                prefix={'pdp'}
                arrowsEnable
                activeSlideIndex={activeSlideIndex}
                onSlideChange={handleSlideChange}
                navigationWrapperClass={s.carouselNavigation}
                contentClassName={s.contentClassName}
                arrowsClassName={s.arrowsClassName}
                badgeContainer={
                  process.env.BROWSER &&
                  (hasVideo || imageBadge) &&
                  <Suspense fallback={<div/>}>
                    <CssCarouselBadges
                      pageTypeForDavinci={pageTypeForDavinci}
                      sku={sku}
                      hasVideo={hasVideo}
                      imageBadge={imageBadge}
                      componentViewEventData={{
                        pageValue: sku,
                        pageType: pageTypeForDavinci,
                        productId,
                        merchantName,
                        merchantId,
                        listingId,
                        price: `${price}`,
                        categoryHierarchy: `${categoryId} - ${categoryName}`,
                      }}
                      videoBadgeOnClick={() => {
                        setVideoPlay(true)
                        setShowVideo(true)
                      }}/>
                  </Suspense>
                }
                thumbnail={
                  <ProductDetailCarouselThumbnails
                    prefix={'pdp'}
                    viewType={componentData?.imageRatio}
                    name={name}
                    images={productMedia?.filter(item => !item.isVideo)}
                    activeSlideIndex={activeSlideIndex}
                    onThumbnailClick={handleSlideChange}/>
                }>
                {
                  productMedia?.filter(item => !item.isVideo).map((image, index) =>
                    <div
                      className={cx(s.imageContainer, {[s.greyScale]: !isAvailableProduct})}
                      key={index}
                      onClick={handleExtendedCarouselClick}>
                      <HbPlaceholderFrame ratio={imageRatio}>
                        <HbImageView
                          className={s.image}
                          retinaSupport={index !== 0}
                          shouldLoadEagerly={index === 0}
                          forcePreload={index === 0}
                          image={{
                            alt: name,
                            src: image.url,
                            componentSize: imageSize,
                            size: imageSizeText,
                            originSize: {width: image?.width, height: image?.height},
                          }}/>
                      </HbPlaceholderFrame>
                    </div>
                  )
                }
              </CssCarousel>
              {isFashionProduct ?
                <div className={s.previewImage} style={{paddingTop: `${100 / imageRatio}%`}} data-test-id={'preview-image'} id={'preview-image'} aria-hidden />
                : null
              }
            </>
        }
      </div>
      {
        showExtendedCarousel &&
        process.env.BROWSER &&
        <Suspense fallback={<div/>}>
          <ProductDetailExtendedCarousel
            prefix={'pdpDetail'}
            isOpen
            viewType={componentData?.imageRatio}
            images={productMedia}
            activeSlideIndex={activeSlideIndex}
            onSlideChange={handleSlideChange}
            onCloseClick={handleExtendedCarouselCloseClick}>
          </ProductDetailExtendedCarousel>
        </Suspense>
      }
      {
        showVideo &&
        process.env.BROWSER &&
        <Suspense fallback={<div/>}>
          <VideoView
            className={s.videoComponent}
            posterSize={imageSize}
            disablePictureInPicture={false}
            preload={'none'}
            controls
            activeOverlay={showVideo}
            onCloseView={() => setShowVideo(false)}
            showPlayIcon={false}
            showCloseIcon
            enableFullScreen={false}
            video={{
              mp4: productMedia?.find(item => item.isVideo)?.link
            }}
            play={videoPlay}
            onPlayChange={handleVideoPlayChange}
            data-test-id={'video-player'}/>
        </Suspense>
      }
    </>
  )
});

ProductDetailCarousel.propTypes = {
  productMedia: PropTypes.array.isRequired,
  isAdultProduct: PropTypes.bool
}

export default ProductDetailCarousel
